import React from "react";
import { useI18n } from "@/i18n/client";
import Link from "next/link";
import useStoreKeys from "@/hooks/useStoreKeys";
import { ProductData, SearchDropdownProduct } from "@/lib/5874/types";
import IntellisuggestProductClickEvent from "@/components/SearchSpring/IntellisuggestProductClickEvent";
import { ProductContextProvider } from "@/providers/ProductContextProvider";
import mapProductOptions from "@/hooks/mapProductOptions";
import { SearchProductSkeleton } from "@/components/UI/Skeleton/SearchSkeleton";
import Skeleton from "@/components/UI/Skeleton/Skeleton";
import CompressedImage from "@/components/UI/CompressedImage/CompressedImage";
import Pricing from "@/components/ProductCard/Pricing/Pricing";
import styles from "./SearchProducts.module.scss";

interface SearchProductsProps {
	datalayerProducts: ProductData[] | undefined;
	searchTerm: string;
	products: SearchDropdownProduct[];
	manageProductClick?: (product: any) => boolean;
	manageCloseDropdown: () => void;
	maxLimit: number;
	isLoading: boolean;
}

const decodeHTML = (html: string) => {
	if (!html) return "";
	const textarea = document.createElement("textarea");
	textarea.innerHTML = html;
	return textarea.value;
};

const SearchProducts = ({
	datalayerProducts,
	searchTerm,
	products,
	manageCloseDropdown,
	maxLimit = 6,
	isLoading,
}: SearchProductsProps) => {
	const t = useI18n();
	const { store, lang } = useStoreKeys();
	const referrer = process.env.DOMAIN ?? "";

	return (
		<div className={styles.products}>
			<div className={styles.header}>{t("search.products")}</div>
			<div className={styles.list}>
				{!isLoading &&
					products?.map((product, index) => {
						// Max of 6 products
						if (index + 1 > maxLimit) return null;

						const url = new URL(product.url);
						const path = url?.pathname.substring(1);
						const datalayerProduct = datalayerProducts?.find((p) => {
							return p.id === parseInt(product.uid);
						});

						const productSpecifications = mapProductOptions([product]);

						return (
							<IntellisuggestProductClickEvent
								key={index}
								datalayerProduct={datalayerProduct}
								product={product}
								referrer={referrer}
								path={path}
								manageCloseDropdown={manageCloseDropdown}
								store={store}
							>
								<ProductContextProvider product={product.bcProduct!}>
									<Link
										className={styles.item}
										key={product?.id}
										href={`/${store}/${lang}/${path}`}
									>
										<CompressedImage
											src={product?.thumbnailImageUrl}
											height={80}
											width={80}
											altText={product?.name}
											containerClass={styles.image}
											withNextImage={false}
										/>
										<div className={styles.details}>
											<div className={styles.top}>
												<span className={styles.brand}>{product?.brand}</span>
												<span className={styles.sku}>{product?.sku}</span>
											</div>
											<span className={styles.name}>
												{decodeHTML(product?.name)}
											</span>
											{productSpecifications &&
												productSpecifications.length > 0 && (
													<div className={styles.specs}>
														{productSpecifications?.map((spec) => (
															<div key={spec.label} className={styles.spec}>
																<span className={styles.label}>
																	{spec.label}:
																</span>
																<span className={styles.value}>
																	{spec.options[0].label}
																</span>
															</div>
														))}
													</div>
												)}
											{product.isPriceLoaded ? (
												<Pricing />
											) : (
												<Skeleton width={100} />
											)}
										</div>
									</Link>
								</ProductContextProvider>
							</IntellisuggestProductClickEvent>
						);
					})}
				{searchTerm && !products.length && !isLoading && (
					<span>{t("search.noResults.products.noProductsFound")}</span>
				)}
				{isLoading && <SearchProductSkeleton />}
			</div>
		</div>
	);
};

export default SearchProducts;
